import { Box, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { modals } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import { zodResolver } from "mantine-form-zod-resolver"
import { useState, useEffect, useMemo } from "react"

import type {
  AxesFeatureLabel,
  AxesFeatureLabelsMetadata,
} from "@costory/types/endpoints/labelsExplorer"
import {
  AxesFeatureLabelSchema,
  isSameAxesFeatureLabel,
  LabelsExplorerResponses,
} from "@costory/types/endpoints/labelsExplorer"

import { useUnsavedChangesBlocker } from "@costory/front/components/common/useUnsavedChangesBlocker"
import { TransformationHeader } from "@costory/front/pages/LabelsExplorer/components/TransformationHeader"
import { TransformationLayout } from "@costory/front/pages/LabelsExplorer/components/TransformationLayout"
import { CombinedLabelsPanel } from "@costory/front/pages/LabelsExplorer/components/combinedLabelsPanel"
import { ResultPreviewPanel } from "@costory/front/pages/LabelsExplorer/components/resultPreviewPanel"
import { StepType } from "@costory/front/pages/LabelsExplorer/components/stepTypes"
import {
  useAxesFeatureLabelsMutation,
  useAxesFeatureLabelsQuery,
} from "@costory/front/queries/labelsExplorer"

interface Step2DeduplicationProps {
  allLabels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  setIngestMode: (ingestMode: boolean) => void
}

export function Step2Deduplication({
  allLabels,
  setIngestMode,
}: Step2DeduplicationProps) {
  // State for current transformations (what's in memory)
  const [axesFeatureLabels, setAxesFeatureLabels] =
    useState<AxesFeatureLabelsMetadata>([])
  const [lastSavedAxesFeatureLabels, setLastSavedAxesFeatureLabels] =
    useState<AxesFeatureLabelsMetadata>([])
  const [editingLabel, setEditingLabel] = useState<AxesFeatureLabel | null>(
    null,
  )

  const [completedSteps, setCompletedSteps] = useState<
    Record<StepType, AxesFeatureLabel | null>
  >({
    [StepType.MERGE]: null,
    [StepType.NAME]: null,
    [StepType.MAP]: null,
  })

  const [openSteps, setOpenSteps] = useState<Record<StepType, boolean>>({
    [StepType.MERGE]: false,
    [StepType.NAME]: false,
    [StepType.MAP]: false,
  })
  // UI state
  const [searchTerm, setSearchTerm] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingAll, setIsDeletingAll] = useState(false)

  const newTransformationForm =
    useForm<LabelsExplorerResponses.AxesFeatureLabelForm>({
      initialValues: {
        name: "",
        columnsMerged: [],
        maps: [],
      },
      validate: zodResolver(AxesFeatureLabelSchema),
    })

  // Query for loading saved transformations
  const { data: savedAxesFeatureLabelsData, isLoading: isLoadingSaved } =
    useAxesFeatureLabelsQuery()
  const { mutateAsync: saveAxesFeatureLabels } = useAxesFeatureLabelsMutation()

  // Load saved transformations on component mount
  useEffect(() => {
    if (savedAxesFeatureLabelsData?.transformations) {
      setAxesFeatureLabels(savedAxesFeatureLabelsData.transformations)
      setLastSavedAxesFeatureLabels(savedAxesFeatureLabelsData.transformations)
    } else {
      setAxesFeatureLabels([])
      setLastSavedAxesFeatureLabels([])
    }
  }, [savedAxesFeatureLabelsData])

  // Check for unsaved changes
  const hasUnsavedChanges = () => {
    if (
      axesFeatureLabels.length === 0 &&
      lastSavedAxesFeatureLabels.length === 0
    ) {
      return false
    }

    const serializeLabel = (label: AxesFeatureLabel) => {
      return JSON.stringify({
        name: label.name,
        columns: label.columnsMerged.map((col) => col.name).sort(),
        maps: label.maps
          .map((map) => {
            return Object.keys(map)
              .sort()
              .reduce(
                (acc, key) => {
                  acc[key as keyof typeof map] = map[key as keyof typeof map]
                  return acc
                },
                {} as Record<string, string>,
              )
          })
          .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
      })
    }

    const currentSerialized = axesFeatureLabels.map(serializeLabel).sort()

    const savedSerialized = lastSavedAxesFeatureLabels
      .map(serializeLabel)
      .sort()

    return JSON.stringify(currentSerialized) !== JSON.stringify(savedSerialized)
  }

  // Check if there's actual work in progress (not just form state)
  const hasActualWorkInProgress = () => {
    return (
      hasUnsavedChanges() ||
      newTransformationForm.values.columnsMerged.length > 0
    )
  }

  // Block navigation when there are unsaved changes
  useUnsavedChangesBlocker({
    isDirtyCallback: hasActualWorkInProgress,
    onConfirmCallback: () => {
      // When user confirms leaving, reset to last saved state
      setAxesFeatureLabels(lastSavedAxesFeatureLabels)
      newTransformationForm.reset()
    },
  })

  const handleSaveTransformations = () => {
    if (axesFeatureLabels.length === 0) return
    modals.openConfirmModal({
      title: "Save Transformations",
      children: (
        <Text size="sm">
          Are you sure you want to save {axesFeatureLabels.length}{" "}
          transformation(s)? This will apply all the configured label
          transformations.
        </Text>
      ),
      labels: { confirm: "Save", cancel: "Cancel" },
      onConfirm: () => {
        confirmSave()
      },
    })
  }

  const confirmSave = async () => {
    setIsSaving(true)
    try {
      await saveAxesFeatureLabels({
        metadata: axesFeatureLabels,
        onSuccess: (metadata) => {
          setLastSavedAxesFeatureLabels(metadata)
          // Navigate back to the original label explorer screen after successful save
          setIngestMode(false)
          showNotification({
            color: "green",
            title: "Transformations Saved",
            message:
              "All transformations have been saved successfully. Redirecting to label explorer...",
          })
        },
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteAxesFeatureLabel = (axesFeatureLabel: AxesFeatureLabel) => {
    modals.openConfirmModal({
      title: "Delete Transformation",
      children: (
        <Text size="sm">
          Are you sure you want to delete this transformation? This action
          cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      onConfirm: () => {
        confirmDelete(axesFeatureLabel)
      },
    })
  }

  const confirmDelete = (axesFeatureLabel: AxesFeatureLabel) => {
    const remainingAxesFeatureLabels = axesFeatureLabels.filter(
      (t) => !isSameAxesFeatureLabel(t, axesFeatureLabel),
    )

    setAxesFeatureLabels(remainingAxesFeatureLabels)

    // If we're deleting all transformations, save the empty state to backend
    if (remainingAxesFeatureLabels.length === 0) {
      setIsDeletingAll(true)
      setLastSavedAxesFeatureLabels([])
      // Auto-save empty state to backend
      saveAxesFeatureLabels({
        metadata: [],
        onSuccess: () => {
          setIsDeletingAll(false)
          // Navigate back to the original label explorer screen after successful auto-save
          setIngestMode(false)
          showNotification({
            color: "green",
            title: "All Transformations Deleted",
            message:
              "All transformations have been deleted and saved. Redirecting to label explorer...",
          })
        },
      }).catch((error: { error: string }) => {
        setIsDeletingAll(false)
        showNotification({
          color: "red",
          title: "Error",
          message: error.error || "Failed to save deletion",
        })
      })
    }
  }

  const addAxesFeatureLabel = (axesFeatureLabel: AxesFeatureLabel) => {
    if (AxesFeatureLabelSchema.safeParse(axesFeatureLabel).error) {
      showNotification({
        color: "red",
        title: "Error",
        message: "Invalid transformation",
      })
      return
    }
    if (
      axesFeatureLabels
        .filter((l) => l.name != editingLabel?.name)
        .map((el) => el.name)
        .includes(axesFeatureLabel.name)
    ) {
      showNotification({
        color: "red",
        title: "Error",
        message: "Transformation with this name already exists",
      })
      return
    }
    let updated = [...axesFeatureLabels]

    if (editingLabel) {
      updated = updated.filter(
        (label) =>
          !(
            label.name === editingLabel.name &&
            JSON.stringify(label.columnsMerged) ===
              JSON.stringify(editingLabel.columnsMerged)
          ),
      )
    }
    updated.push(axesFeatureLabel)
    setAxesFeatureLabels(updated)
    setEditingLabel(null)

    newTransformationForm.reset()
  }

  const handleDiscardChanges = () => {
    // If there's no work in progress, just go back without confirmation
    if (!hasActualWorkInProgress()) {
      setIngestMode(false)
      return
    }

    modals.openConfirmModal({
      title: "Discard Changes",
      children: (
        <Text size="sm">
          Are you sure you want to discard all unsaved changes? Any unsaved
          progress will be lost.
        </Text>
      ),
      labels: { confirm: "Discard", cancel: "Cancel" },
      onConfirm: () => {
        // Reset to last saved state
        setAxesFeatureLabels(lastSavedAxesFeatureLabels)
        // Reset the form
        newTransformationForm.reset()
        showNotification({
          color: "blue",
          title: "Changes Discarded",
          message: "All unsaved changes have been discarded",
        })
      },
    })
  }

  const filteredLabels = useMemo(() => {
    // Get all transformed label names
    const transformedLabelNames = new Set<string>()
    axesFeatureLabels.forEach((transformation) => {
      transformation.columnsMerged.forEach((column) => {
        transformedLabelNames.add(column.name)
      })
    })

    return allLabels
      .filter((label) => {
        // Filter by search term
        const matchesSearch = label.key
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        // Filter out already transformed labels
        const notTransformed = !transformedLabelNames.has(label.key)
        return matchesSearch && notTransformed
      })
      .sort(
        (a, b) =>
          (b.total_cost_key_present ?? 0) - (a.total_cost_key_present ?? 0),
      )
  }, [allLabels, searchTerm, axesFeatureLabels])

  // Show loading state while fetching saved transformations
  if (isLoadingSaved) {
    return (
      <Box
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 80px)",
        }}
      >
        <Text size="lg" c="dimmed">
          Loading saved transformations...
        </Text>
      </Box>
    )
  }

  return (
    <TransformationLayout
      header={
        <TransformationHeader
          transformationsCount={axesFeatureLabels.length}
          hasUnsavedChanges={hasUnsavedChanges()}
          hasWorkInProgress={hasActualWorkInProgress()}
          onSave={handleSaveTransformations}
          onDiscard={handleDiscardChanges}
          isSaving={isSaving}
          isDeletingAll={isDeletingAll}
        />
      }
      leftPanel={
        <CombinedLabelsPanel
          form={newTransformationForm}
          labels={filteredLabels}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAxesFeatureLabelAdd={addAxesFeatureLabel}
          allLabels={allLabels}
          completedSteps={completedSteps}
          setCompletedSteps={setCompletedSteps}
          openSteps={openSteps}
          setOpenSteps={setOpenSteps}
          editingLabel={editingLabel}
        />
      }
      rightPanel={
        <ResultPreviewPanel
          form={newTransformationForm}
          initialLabels={allLabels.filter((l) => !l.ingested)}
          axesFeatureLabels={axesFeatureLabels}
          onDeleteAxesFeatureLabel={handleDeleteAxesFeatureLabel}
          setCompletedSteps={setCompletedSteps}
          setOpenSteps={setOpenSteps}
          setEditingLabel={setEditingLabel}
        />
      }
    />
  )
}
